import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Send, AlertTriangle, ChevronDown, ExternalLink, Loader2, Puzzle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../lib/theme';
import { listQbConnections, publishCheckReceipt, publishReceipt } from '../../lib/api';
import PublishStatusBadge from './PublishStatusBadge';

/**
 * Phase 25.2 — Primary publish action on receipt detail.
 *
 * States:
 *   • No connections: disabled + helper link to Settings → Integrations
 *   • Already published to a connection: shows PublishStatusBadge + "Publish again" (greyed)
 *   • Ready: button (or dropdown picker with 2+ companies). Click runs
 *     publish-check, surfaces blocking issues inline, or shows the
 *     Purchase payload preview + "Confirm publish".
 */
export default function PublishToQbButton({ receipt, onPublished, onToast }) {
  const { colors } = useTheme();
  const [connections, setConnections] = useState([]);
  const [loadingConns, setLoadingConns] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [checkState, setCheckState] = useState(null); // { ok, issues, warnings, payload_preview }
  const [checking, setChecking] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [confirmRepublish, setConfirmRepublish] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await listQbConnections();
        setConnections(data.connections || []);
        if ((data.connections || []).length) setSelectedId(data.connections[0].id);
      } finally { setLoadingConns(false); }
    })();
  }, []);

  const publishedConnection = useMemo(() => {
    const qp = receipt?.qb_publish;
    if (!qp || qp.status !== 'published') return null;
    return connections.find((c) => c.id === qp.connection_id) || null;
  }, [receipt?.qb_publish, connections]);

  const selectedConn = useMemo(
    () => connections.find((c) => c.id === selectedId) || null,
    [connections, selectedId],
  );

  const runCheck = useCallback(async () => {
    if (!selectedConn) return;
    setChecking(true);
    setShowPreview(false);
    try {
      const data = await publishCheckReceipt(selectedConn.id, receipt.id);
      setCheckState(data);
      if (data.ok) setShowPreview(true);
    } catch (e) {
      onToast?.(`Could not run publish-check: ${e.detail || 'network error'}`);
    } finally {
      setChecking(false);
    }
  }, [selectedConn, receipt?.id, onToast]);

  const confirmPublish = async () => {
    if (!selectedConn) return;
    setPublishing(true);
    try {
      const data = await publishReceipt(selectedConn.id, receipt.id);
      onToast?.(`Published to ${selectedConn.company_name} — QB# ${data.qb_transaction_id}`);
      setCheckState(null);
      onPublished?.(data);
    } catch (e) {
      const detail = e.detail;
      if (typeof detail === 'object' && detail?.code === 'validation_failed') {
        setCheckState({ ok: false, issues: detail.issues, warnings: [], payload_preview: null });
        onToast?.('Validation failed — see issues below.');
      } else {
        onToast?.(`Publish failed: ${typeof detail === 'string' ? detail : (detail?.message || 'error')}`);
      }
    } finally {
      setPublishing(false);
    }
  };

  const alreadyToSelected = publishedConnection && publishedConnection.id === selectedId;

  // --- empty-state: no connections ---
  if (!loadingConns && connections.length === 0) {
    return (
      <div
        data-testid="publish-qb-empty"
        className="rounded-xl p-4 mb-4"
        style={{
          background: colors.surface,
          border: `1px dashed ${colors.border}`,
        }}
      >
        <div className="flex items-center gap-3">
          <div style={{
            width: 40, height: 40, borderRadius: 10,
            background: colors.bg,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: colors.textMuted,
          }}>
            <Puzzle size={18} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: colors.text }}>
              Publish to QuickBooks
            </div>
            <div style={{ color: colors.textMuted, fontSize: 12.5, marginTop: 2 }}>
              Connect a QuickBooks company in{' '}
              <Link to="/app/settings#integrations" style={{ color: colors.accent, fontWeight: 600 }}>
                Settings → Integrations
              </Link>{' '}
              to enable publishing.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="publish-qb-card"
      className="rounded-xl p-4 mb-4"
      style={{
        background: 'linear-gradient(135deg, rgba(44,160,28,0.07) 0%, rgba(0,0,0,0) 100%)',
        border: `1px solid ${colors.border}`,
        borderLeft: `3px solid #2CA01C`,
      }}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <p style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#2CA01C' }}>
            QuickBooks Publishing
          </p>
          <p style={{ fontSize: 15, fontWeight: 700, color: colors.text, marginTop: 2 }}>
            {publishedConnection ? 'Published' : 'Ready to publish'}
          </p>
        </div>
        {publishedConnection && (
          <PublishStatusBadge qbPublish={receipt.qb_publish} connection={publishedConnection} />
        )}
      </div>

      {/* Connection picker when 2+ companies */}
      {connections.length > 1 && (
        <div className="mb-3" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <label style={{ fontSize: 12, color: colors.textMuted, fontWeight: 600 }}>Company:</label>
          <select
            data-testid="publish-qb-conn-select"
            value={selectedId || ''}
            onChange={(e) => { setSelectedId(e.target.value); setCheckState(null); setShowPreview(false); }}
            style={{
              padding: '6px 10px', borderRadius: 8,
              border: `1px solid ${colors.border}`,
              background: colors.surface, color: colors.text,
              fontSize: 13, fontWeight: 600,
              minWidth: 220,
            }}
          >
            {connections.map((c) => (
              <option key={c.id} value={c.id}>{c.company_name}</option>
            ))}
          </select>
        </div>
      )}

      {/* ACTION ROW */}
      {!publishedConnection && (
        <button
          type="button"
          data-testid="publish-qb-btn"
          onClick={runCheck}
          disabled={checking || !selectedConn}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '9px 16px', borderRadius: 10,
            background: '#2CA01C', color: 'white',
            fontSize: 13, fontWeight: 700, letterSpacing: '0.01em',
            border: 'none', cursor: checking ? 'wait' : 'pointer',
            opacity: !selectedConn ? 0.6 : 1,
          }}
        >
          {checking ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          {selectedConn ? `Publish to ${selectedConn.company_name}` : 'Publish to QuickBooks'}
        </button>
      )}

      {publishedConnection && !confirmRepublish && (
        <button
          type="button"
          data-testid="publish-qb-again-btn"
          onClick={() => setConfirmRepublish(true)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 7,
            padding: '7px 12px', borderRadius: 9,
            background: 'transparent', color: colors.textMuted,
            fontSize: 12, fontWeight: 600,
            border: `1px solid ${colors.border}`, cursor: 'pointer',
          }}
        >
          Publish again…
        </button>
      )}

      {publishedConnection && confirmRepublish && !alreadyToSelected && (
        <div style={{ marginTop: 8, padding: 10, borderRadius: 8, background: colors.bg, fontSize: 12, color: colors.text }}>
          This will publish a <strong>new</strong> transaction in {selectedConn?.company_name || 'QuickBooks'}.
          <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
            <button
              data-testid="publish-qb-again-confirm"
              onClick={runCheck}
              style={{ padding: '6px 12px', borderRadius: 7, background: '#2CA01C', color: 'white', border: 'none', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
            >
              Yes, publish again
            </button>
            <button
              onClick={() => setConfirmRepublish(false)}
              style={{ padding: '6px 12px', borderRadius: 7, background: 'transparent', color: colors.textMuted, border: `1px solid ${colors.border}`, fontSize: 12, cursor: 'pointer' }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {publishedConnection && confirmRepublish && alreadyToSelected && (
        <div style={{ marginTop: 8, padding: 10, borderRadius: 8, background: '#FFF4D6', color: '#8A5A00', fontSize: 12 }}>
          Already published to this company. Pick a different company to publish elsewhere.
        </div>
      )}

      {/* VALIDATION ISSUES */}
      {checkState && !checkState.ok && (
        <div
          data-testid="publish-qb-issues"
          style={{
            marginTop: 12, padding: 12, borderRadius: 10,
            background: '#FDECEC', color: '#8C1E12',
            border: '1px solid #F4CAC3',
            fontSize: 13,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontWeight: 700, marginBottom: 6 }}>
            <AlertTriangle size={14} />
            Can&apos;t publish yet
          </div>
          <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {checkState.issues.map((i) => (
              <li key={i.code} data-testid={`publish-qb-issue-${i.code}`} style={{ display: 'flex', gap: 7 }}>
                <span>•</span>
                <span>{i.message}</span>
              </li>
            ))}
          </ul>
          <div style={{ marginTop: 8 }}>
            <Link
              to="/app/settings#integrations"
              style={{ fontSize: 12, color: '#8C1E12', fontWeight: 700, textDecoration: 'underline' }}
            >
              Open mappings →
            </Link>
          </div>
        </div>
      )}

      {/* PREVIEW + CONFIRM */}
      {checkState?.ok && (
        <div
          data-testid="publish-qb-preview"
          style={{
            marginTop: 12, padding: 12, borderRadius: 10,
            background: colors.surface, border: `1px solid ${colors.border}`,
          }}
        >
          <button
            type="button"
            onClick={() => setShowPreview((v) => !v)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'transparent', color: colors.textMuted,
              border: 'none', cursor: 'pointer', fontSize: 11.5, fontWeight: 700,
              padding: 0,
            }}
          >
            <ChevronDown size={12} style={{ transform: showPreview ? 'rotate(0deg)' : 'rotate(-90deg)' }} />
            Preview QuickBooks payload
          </button>
          {showPreview && (
            <pre
              style={{
                marginTop: 8, padding: 10, borderRadius: 8,
                background: colors.bg, color: colors.text,
                fontSize: 11, fontFamily: 'ui-monospace, Menlo, monospace',
                overflow: 'auto', maxHeight: 280,
              }}
            >{JSON.stringify(checkState.payload_preview, null, 2)}</pre>
          )}
          {checkState.warnings?.length > 0 && (
            <ul style={{ marginTop: 8, color: '#8A5A00', fontSize: 12, display: 'flex', flexDirection: 'column', gap: 4, listStyle: 'none', padding: 0 }}>
              {checkState.warnings.map((w) => (
                <li key={w.code}>⚠ {w.message}</li>
              ))}
            </ul>
          )}
          <div style={{ marginTop: 10 }}>
            <button
              type="button"
              data-testid="publish-qb-confirm"
              onClick={confirmPublish}
              disabled={publishing}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 7,
                padding: '9px 16px', borderRadius: 9,
                background: '#2CA01C', color: 'white',
                fontSize: 13, fontWeight: 700,
                border: 'none', cursor: publishing ? 'wait' : 'pointer',
              }}
            >
              {publishing ? <Loader2 size={13} className="animate-spin" /> : <ExternalLink size={13} />}
              {publishing ? 'Publishing…' : 'Confirm publish'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
